import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useMutation, useSubscription } from '@apollo/client';
import { Box, Flex, FlexProps, StyleProps, Tab, TabList, TabPanels, Tabs } from '@chakra-ui/react';
import { getIn, useFormikContext } from 'formik';
import { DebouncedFunc } from 'lodash';
import isEqual from 'lodash/isEqual';
import { toast } from 'react-toastify';

import { Deal, dealInfoUpsert, onAICloser } from '../../gql/dealGql';
import { stripDeprecatedFinancialInfoFields } from '../../gql/financialInfoGql';
import { Bank, Maybe, useOnDealLockStatusSubscription } from '../../gql/generated/graphql';

import Autosaving from '../CreditApplication/components/Autosaving';
import CardHeaderV2 from '../shared/Card/components/CardHeaderV2';
import { SwitchesSection } from './components/SwitchesSection';

import { DealStructurePanel } from './DealStructurePanel';
import { TitleInfoPanel } from './TitleInfoPanel';
import { CUSTOM_DATE_FIELDS, isCustomDateField } from './constants';
import { validationSchema } from './validationSchema';

import { isTitling, shouldDefaultToTitleInfo } from '../../constants/dealStates';
import useDebounce from '../../hooks/useDebounce';
import { useSetValuesByDealType } from '../../hooks/useSetValuesByDealType';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import { AbilityContext } from '../../libs/contextLib';
import { removeFailedValidation } from '../../libs/utils';
import DealStatesModal from '../../pages/DealDetail/DealStatesModal';
import { cleanCustomerForUpsert } from '../../utils/deals';

export const sectionsContainerProps: FlexProps = {
  gap: 8,
  p: 4,
  direction: { base: 'column', xl: 'row' },
};
export const sectionWidth: StyleProps['w'] = { base: '100%', xl: '50%' };
export const dividerProps: FlexProps = {
  borderColor: 'gray.100',
  borderWidth: '1px',
};

const processorField = 'financial_info.processor';
const baseTaxAmountField = 'financial_info.base_tax_amount';
const newRegistrationFee = 'financial_info.new_registration_fee';
const registrationTransferFee = 'financial_info.registration_transfer_fee';
const quickNotes = 'financial_info.quick_notes';
const buyRateField = 'financial_info.buy_rate';

const manuallySaveFields = [
  ...Object.values(CUSTOM_DATE_FIELDS),
  processorField,
  baseTaxAmountField,
  newRegistrationFee,
  registrationTransferFee,
  quickNotes,
  buyRateField,
];

const newBaseTaxAmountField = 'financial_info.newBaseTaxAmount';
const maxMarkupField = 'financial_info.maxMarkup';
const tempUserEnteredReserveField = 'financial_info.tempUserEnteredReserve';
const newBuyRateField = 'financial_info.newBuyRate';
const doubleTaxAppliedField = 'car.payoff.doubleTaxApplied';
const previousTotalPayoffField = 'car.payoff.previousTotalPayoff';
const totalPayoffField = 'car.payoff.totalPayoff';

const frontendPayoffFields = [doubleTaxAppliedField, previousTotalPayoffField, totalPayoffField];
const frontendOnlyFields = [
  newBaseTaxAmountField,
  maxMarkupField,
  tempUserEnteredReserveField,
  newBuyRateField,
  ...frontendPayoffFields,
];

const actualReserveField = 'financial_info.actual_reserve';
const bankFeesField = 'financial_info.bank_fees';
const userEnteredReserveField = 'financial_info.user_entered_reserve';
const cardPaymentAmountLimitField = 'financial_info.credit_card_payment_amount_limit';
const needsTemporaryRegistrationTagsField = 'financial_info.needs_temporary_registration_tags';
const titleRegistrationOptionField = 'financial_info.title_registration_option';
const transferCustomPlatesField = 'financial_info.plate_transfer';
const buyerNotLesseeField = 'financial_info.buyer_not_lessee';
const mailingAddressFields = [
  'customer.mailing_address.address_type',
  'customer.mailing_address.address_line',
  'customer.mailing_address.address_line_2',
  'customer.mailing_address.city',
  'customer.mailing_address.state',
  'customer.mailing_address.county',
  'customer.mailing_address.zip',
];
const forceSaveFields = [
  actualReserveField,
  bankFeesField,
  userEnteredReserveField,
  cardPaymentAmountLimitField,
  needsTemporaryRegistrationTagsField,
  transferCustomPlatesField,
  buyerNotLesseeField,
  // `titleRegistrationOptionField` is here as a workaround and not because it can be saved after `closed`.
  // `tempUserEnteredReserveField` also changes at the same time blocking the auto-save.
  titleRegistrationOptionField,
  ...mailingAddressFields,
];

type DebouncedSaveArgs = {
  newValues?: Deal;
  forceSave?: boolean;
};
export type DebouncedSave = DebouncedFunc<(args?: DebouncedSaveArgs) => Promise<void>>;

type DealInfoBuyoutFormProps = {
  banks: Bank[];
  isEditingWithPermission: boolean;
  setIsEditingWithPermission: Dispatch<SetStateAction<boolean>>;
  isEditable: boolean;
};

const highlightStyle = {
  '@keyframes flashAnimation': {
    '0%, 100%': { backgroundColor: 'transparent' },
    '50%': { backgroundColor: 'rgba(255, 50, 50, 0.8)' },
  },
  '.field-highlight': {
    animation: 'flashAnimation 0.3s ease 3',
  },
};

export const DealInfoBuyoutForm = ({
  banks,
  isEditingWithPermission,
  setIsEditingWithPermission,
  isEditable,
}: DealInfoBuyoutFormProps) => {
  const { deal, dispatch, userChangedSomething, setUserChangedSomething } = useContext(DealContext);
  const abilities = useContext(AbilityContext);
  const { values, handleChange, resetForm } = useFormikContext<Deal>();

  const [autosaving, setAutosaving] = useState(false);
  const [saveTime, setSaveTime] = useState('');
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    shouldDefaultToTitleInfo(deal.state) ? 1 : 0,
  );

  const [upsertDealInfo] = useMutation<{ dealInfoUpsert: Maybe<Deal> }>(dealInfoUpsert);

  // Snapshot of the last values the autosave effect processed, reduced to what
  // dealInfoUpsert actually saves (everything else is stripped). Lets the effect
  // tell a real edit apart from a product-only change.
  const prevAutosavePayloadRef = useRef<{
    financialInfo: object;
    car: Deal['car'];
    customer: Deal['customer'];
  } | null>(null);

  const dealIsInTitlingState = isTitling(deal.state);
  // `useEffect` that sets the default values when the deal type changes.
  useSetValuesByDealType({ banks });

  const autosaveTimer = () => {
    setSaveTime(new Date().toLocaleTimeString('en-US'));
    setAutosaving(true);
    setTimeout(() => setAutosaving(false), 900);
  };

  const debouncedSave = useDebounce(
    async ({ newValues, forceSave = false }: DebouncedSaveArgs = {}) => {
      if (!isEditable && !forceSave) {
        return;
      }

      // In some cases the `values` in the Formik state are not updated yet.
      const valuesToSave = newValues ?? values;
      const valuesToSaveWithoutFailures = removeFailedValidation(valuesToSave, validationSchema, {
        deal,
        abilities,
      });
      autosaveTimer();
      try {
        const { data } = await upsertDealInfo({
          variables: {
            financialInfo: stripDeprecatedFinancialInfoFields(
              valuesToSaveWithoutFailures.financial_info,
            ),
            car: valuesToSaveWithoutFailures.car,
            customer: cleanCustomerForUpsert(valuesToSaveWithoutFailures.customer),
          },
        });

        if (!data?.dealInfoUpsert) {
          toast.error('Failed to update Deal Info');
          return;
        }

        dispatch({ type: DealActionsEnum.UpdateDeal, payload: data.dealInfoUpsert });
      } catch (error) {
        toast.error('Failed to update Deal Info');
        logger.error('DealInfoBuyoutForm.tsx', 'Failed to update Deal Info', null, error);
      }
    },
  );

  const customHandleChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement> | undefined,
  ) => {
    setUserChangedSomething(true);
    handleChange(e);
  };

  // We can't call `debouncedSave` on the `customHandleChange` because values are not updated yet.
  useEffect(() => {
    // `setFieldTouched` makes the form dirty so we can't use `dirty` it here.
    // If we can remove those and trigger initial validations in a different way, `customHandleChange` can be removed.
    if (!userChangedSomething) {
      return;
    }

    // A product grid / Calc-button selection only mutates the financial_info
    // product fields that dealInfoUpsert strips before saving, so autosaving on
    // those changes persists nothing but races the product save over
    // deal_products — the cause of the intermittent "save didn't stick". Skip
    // when the effective (stripped) save payload is unchanged from last time.
    const autosavePayload = {
      financialInfo: stripDeprecatedFinancialInfoFields(values.financial_info),
      car: values.car,
      customer: values.customer,
    };
    const onlyStrippedFieldsChanged =
      prevAutosavePayloadRef.current != null &&
      isEqual(autosavePayload, prevAutosavePayloadRef.current);
    prevAutosavePayloadRef.current = autosavePayload;
    if (onlyStrippedFieldsChanged) {
      return;
    }

    const someManuallySavedFieldChanged = manuallySaveFields.reduce((isChanged, fieldName) => {
      if (isCustomDateField(fieldName)) {
        const dealDate = getIn(deal, fieldName);
        const dealISODate = dealDate ? new Date(dealDate).toISOString() : null;

        const valuesDate = getIn(values, fieldName);
        const valuesISODate = valuesDate ? new Date(valuesDate).toISOString() : null;

        return isChanged || dealISODate !== valuesISODate;
      }

      return isChanged || getIn(deal, fieldName) !== getIn(values, fieldName);
    }, false);

    const someFrontendOnlyFieldChanged = frontendOnlyFields.reduce((isChanged, fieldName) => {
      const dealValue = getIn(deal, fieldName);
      const valuesValue = getIn(values, fieldName);

      // Workaround for some payoff related fields being undefined in the context deal and blocking the save.
      return isChanged || (dealValue !== undefined && dealValue !== valuesValue);
    }, false);

    const someForceSaveFieldChanged = forceSaveFields.reduce(
      (isChanged, fieldName) => isChanged || getIn(deal, fieldName) !== getIn(values, fieldName),
      false,
    );
    if (
      (someManuallySavedFieldChanged || someFrontendOnlyFieldChanged) &&
      !someForceSaveFieldChanged
    ) {
      return;
    }

    debouncedSave({ newValues: values, forceSave: someForceSaveFieldChanged });
  }, [values]);

  useSubscription(onAICloser, {
    variables: { dealId: deal.id },
    skip: !deal.id,
    onData: ({ data }) => {
      if (!data.data?.onAICloser || data.data.onAICloser.id !== deal.id) {
        return;
      }

      const newData = data.data.onAICloser;
      const fieldsToCheck = ['money_down', 'term', 'option_type', 'sell_rate'] as const;

      // Get references to the form fields
      fieldsToCheck.forEach((field) => {
        const fieldName = `financial_info.${field}`;
        const element = document.getElementsByName(fieldName)[0];
        if (element) {
          const currentValue = getIn(values, fieldName);
          const newValue = getIn(newData, fieldName);

          if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
            element.closest('.chakra-input')?.classList.add('field-highlight');
            // Remove highlight after 3 seconds
            setTimeout(() => {
              element.closest('.chakra-input')?.classList.remove('field-highlight');
            }, 3000);
          }
        }
      });

      resetForm({ values: newData });
    },
  });

  useOnDealLockStatusSubscription({
    skip: !deal.id,
    variables: { dealId: Number(deal.id) },
    onData: ({ data }) => {
      if (!data.data?.onDealLockStatus) {
        return;
      }
      const { dealId, lockStatus } = data.data.onDealLockStatus;
      dispatch({
        type: DealActionsEnum.UpdateDeal,
        payload: { id: dealId, lock_status: lockStatus },
      });
    },
  });

  return (
    <Box sx={highlightStyle}>
      <CardHeaderV2 title="Deal Details" showPodColor pod={deal.pod} variant="rounded">
        <Autosaving autosaving={autosaving} saveTime={saveTime} />
        <DealStatesModal />
      </CardHeaderV2>
      <Tabs
        index={selectedTabIndex}
        onChange={(index) => setSelectedTabIndex(index)}
        variant="enclosed"
      >
        <TabList bgColor="queenBlue" pt={1}>
          <Tab
            bgColor={selectedTabIndex === 0 ? 'white' : 'gray.200'}
            borderRadius="8px 8px 0 0"
            fontWeight="bold"
            ml={1}
            px={8}
          >
            Deal Structure
          </Tab>
          {dealIsInTitlingState ? (
            <Tab
              bgColor={selectedTabIndex === 1 ? 'white' : 'gray.200'}
              borderRadius="8px 8px 0 0"
              fontWeight="bold"
              ml={1}
              px={8}
            >
              Title Info
            </Tab>
          ) : null}
        </TabList>
        <TabPanels>
          <DealStructurePanel
            debouncedSave={debouncedSave}
            banks={banks}
            isEditable={isEditable}
            isEditingWithPermission={isEditingWithPermission}
            setIsEditingWithPermission={setIsEditingWithPermission}
            customHandleChange={customHandleChange}
          />

          {dealIsInTitlingState ? (
            <TitleInfoPanel
              debouncedSave={debouncedSave}
              autosaveTimer={autosaveTimer}
              customHandleChange={customHandleChange}
            />
          ) : null}
        </TabPanels>
      </Tabs>

      <Flex direction="row" {...dividerProps} />
      <SwitchesSection isEditable={isEditable} customHandleChange={customHandleChange} />

      <Flex direction="row" {...dividerProps} />
    </Box>
  );
};
